import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, faFileText, faTable, faUsers, faChartBar, faFileExcel, 
  faCheck, faFilter, faInfoCircle, faCog, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import api from '../services/api';

interface ExportOptions {
  tables: {
    articles: boolean;
    nutritionalValues: boolean;
    units: boolean;
    barcodes: boolean;
  };
  filters: {
    supplierId: string;
    articleNumbers: string;
    gtins: string;
  };
  format: 'json' | 'excel';
}

interface Supplier {
  _id: string;
  name: string;
  kreditorNumber: string;
}

const DataExport: React.FC = () => {
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    tables: {
      articles: true,
      nutritionalValues: false,
      units: false,
      barcodes: false
    },
    filters: {
      supplierId: '',
      articleNumbers: '',
      gtins: ''
    },
    format: 'excel'
  });

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/suppliers');
      const suppliersData = response.data.suppliers || response.data;
      const filteredSuppliers = Array.isArray(suppliersData) 
        ? suppliersData.filter((s: Supplier) => s.kreditorNumber)
        : [];
      setSuppliers(filteredSuppliers);
    } catch (error) {
      console.error('Fehler beim Laden der Lieferanten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableToggle = (table: keyof ExportOptions['tables']) => {
    setExportOptions(prev => ({
      ...prev,
      tables: {
        ...prev.tables,
        [table]: !prev.tables[table]
      }
    }));
  };

  const handleFilterChange = (filter: keyof ExportOptions['filters'], value: string) => {
    setExportOptions(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filter]: value
      }
    }));
  };

  const resetFilters = () => {
    setExportOptions(prev => ({
      ...prev,
      filters: {
        supplierId: '',
        articleNumbers: '',
        gtins: ''
      }
    }));
  };

  const getSelectedTablesCount = () => {
    return Object.values(exportOptions.tables).filter(Boolean).length;
  };

  const buildExportUrl = (endpoint: string) => {
    const params = new URLSearchParams();
    
    if (exportOptions.filters.supplierId) {
      params.append('supplierId', exportOptions.filters.supplierId);
    }
    
    if (exportOptions.filters.articleNumbers.trim()) {
      const articleNums = exportOptions.filters.articleNumbers
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      articleNums.forEach(num => params.append('articleNumbers', num));
    }
    
    if (exportOptions.filters.gtins.trim()) {
      const gtins = exportOptions.filters.gtins
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      gtins.forEach(gtin => params.append('gtins', gtin));
    }
    
    params.append('format', exportOptions.format);
    
    return `/export/perfion/${endpoint}?${params.toString()}`;
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await api.get(url, {
        responseType: 'blob'
      });

      const blob = response.data;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  const exportSingleTable = async (table: string, displayName: string) => {
    setIsExporting(true);
    setExportStatus(`Exportiere ${displayName}...`);
    
    try {
      const url = buildExportUrl(table);
      const filename = `perfion_${table}_export.${exportOptions.format === 'excel' ? 'xlsx' : 'json'}`;
      
      await downloadFile(url, filename);
      setExportStatus(`✅ ${displayName} erfolgreich exportiert!`);
      
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      setExportStatus(`❌ Fehler beim Export von ${displayName}`);
      setTimeout(() => setExportStatus(''), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const exportAllSelected = async () => {
    if (getSelectedTablesCount() === 0) return;
    
    setIsExporting(true);
    setExportStatus('Exportiere alle ausgewählten Tabellen...');
    
    try {
      const url = buildExportUrl('all');
      const filename = `perfion_complete_export.${exportOptions.format === 'excel' ? 'xlsx' : 'json'}`;
      
      await downloadFile(url, filename);
      setExportStatus('✅ Alle Daten erfolgreich exportiert!');
      
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      setExportStatus('❌ Fehler beim Komplett-Export');
      setTimeout(() => setExportStatus(''), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const tableConfigs = [
    {
      key: 'articles' as const,
      name: 'Artikel-Stammdaten',
      description: 'Komplette Artikel-Informationen mit allen Feldern',
      icon: faTable,
      endpoint: 'articles',
      color: 'blue'
    },
    {
      key: 'nutritionalValues' as const,
      name: 'Nährwerte',
      description: 'Detaillierte Nährwert-Informationen pro Artikel',
      icon: faChartBar,
      endpoint: 'nutritional-values',
      color: 'green'
    },
    {
      key: 'units' as const,
      name: 'Einheiten',
      description: 'Verpackungseinheiten und Abmessungen',
      icon: faFileText,
      endpoint: 'units',
      color: 'orange'
    },
    {
      key: 'barcodes' as const,
      name: 'Barcodes',
      description: 'GTIN/Barcode-Informationen',
      icon: faUsers,
      endpoint: 'barcodes',
      color: 'purple'
    }
  ];

  if (loading) {
    return <LoadingSpinner size="medium" text="Daten werden geladen..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* Title */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900"></h1>
            </div>
            <p className="text-gray-600 text-sm mt-1">
            </p>
          </div>
          
          {/* Format Selection and Export Button */}
          <div className="flex gap-2">
            <select
              value={exportOptions.format}
              onChange={(e) => setExportOptions(prev => ({...prev, format: e.target.value as 'excel' | 'json'}))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="excel">Excel (.xlsx)</option>
              <option value="json">JSON (.json)</option>
            </select>
            
            <button
              onClick={exportAllSelected}
              disabled={getSelectedTablesCount() === 0 || isExporting}
              className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-1.5 text-xs" />
              {isExporting ? 'Exportiere...' : 'Export starten'}
            </button>
          </div>
        </div>
      </div>

      {/* Export Status */}
      <AnimatePresence>
        {exportStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mx-4 mt-4"
          >
            <div className={`p-3 border rounded-md ${
              exportStatus.includes('✅') 
                ? 'bg-green-50 border-green-200' 
                : exportStatus.includes('❌')
                ? 'bg-red-50 border-red-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                {isExporting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                )}
                <p className={`text-sm font-medium ${
                  exportStatus.includes('✅') 
                    ? 'text-green-800' 
                    : exportStatus.includes('❌')
                    ? 'text-red-800'
                    : 'text-blue-800'
                }`}>
                  {exportStatus}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Area */}
      <div className="p-4">
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFilter} className="text-red-500" />
                <h2 className="text-lg font-semibold text-gray-900">Filter (Optional)</h2>
              </div>
              <button
                onClick={resetFilters}
                className="px-3 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors text-sm"
                title="Filter zurücksetzen"
              >
                <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                Zurücksetzen
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lieferant auswählen
                </label>
                <select
                  value={exportOptions.filters.supplierId}
                  onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm"
                >
                  <option value="">Alle Lieferanten</option>
                  {suppliers.map(supplier => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.name} ({supplier.kreditorNumber})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Artikel-Nummern
                </label>
                <input
                  type="text"
                  placeholder="123456, 789012 (kommagetrennt)"
                  value={exportOptions.filters.articleNumbers}
                  onChange={(e) => handleFilterChange('articleNumbers', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GTINs
                </label>
                <input
                  type="text"
                  placeholder="4001724035558, 123456789012"
                  value={exportOptions.filters.gtins}
                  onChange={(e) => handleFilterChange('gtins', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTable} className="text-red-500" />
                <h2 className="text-lg font-semibold text-gray-900">Zu exportierende Tabellen</h2>
                <span className="text-sm text-gray-600">
                  ({getSelectedTablesCount()} ausgewählt)
                </span>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {tableConfigs.map((config, index) => {
                const isSelected = exportOptions.tables[config.key];
                
                return (
                  <motion.div
                    key={config.key}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-150 ${
                      isSelected 
                        ? 'border-red-400 bg-red-50' 
                        : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleTableToggle(config.key)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-md ${
                          isSelected ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <FontAwesomeIcon icon={config.icon} className="text-lg" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-base mb-1">
                            {config.name}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {config.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {isSelected && (
                          <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                            <FontAwesomeIcon icon={faCheck} className="text-xs" />
                            Ausgewählt
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            exportSingleTable(config.endpoint, config.name);
                          }}
                          disabled={isExporting}
                          className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FontAwesomeIcon icon={faDownload} className="mr-1" />
                          Einzeln
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExport;